import {
  Bold,
  Code,
  Eye,
  Heading,
  Image as ImageIcon,
  Italic,
  List,
  ListOrdered,
  Pencil,
  SquareCode,
} from 'lucide-react';
import { useId, useRef, useState, type ClipboardEvent, type DragEvent, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useErrorText, useT } from '../../i18n';
import { cn } from '../../lib/cn';
import {
  imageFilesFrom,
  isSupportedImage,
  isWithinSizeLimit,
  uploadAttachment,
} from '../../features/attachments/api';
import { Spinner } from '../ui/spinner';
import { Textarea } from '../ui/input';
import { RichText } from './RichText';
import { isBlank } from './markdown';

/** A toolbar action: what it puts around (or in front of) the selection. */
type Action =
  | { kind: 'wrap'; before: string; after: string }
  | { kind: 'prefix'; prefix: string }
  | { kind: 'block'; fence: string };

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Project the screenshots are uploaded to. */
  projectCode: string;
  id?: string;
  rows?: number;
  invalid?: boolean;
  placeholder?: string;
  'aria-describedby'?: string;
  /** For fields that have no visible label of their own. */
  'aria-label'?: string;
}

const ToolbarButton = ({
  label,
  onClick,
  children,
  active = false,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  active?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    aria-pressed={active}
    className={cn(
      'inline-flex size-7 items-center justify-center rounded transition-colors',
      'text-ink-subtle hover:bg-sunken hover:text-ink',
      'disabled:cursor-not-allowed disabled:opacity-50',
      active ? 'bg-sunken text-ink' : '',
    )}
  >
    {children}
  </button>
);

/**
 * Small Markdown editor used for every free-text field of a test case.
 *
 * It stays a plain textarea on purpose: what is typed is what is stored, the
 * toolbar only inserts the markers, and a preview shows the result. Screenshots
 * are uploaded on paste, drop or pick, and referenced from the text — so a
 * field holds text, never binary.
 */
export const RichTextEditor = ({
  value,
  onChange,
  projectCode,
  id,
  rows = 5,
  invalid = false,
  placeholder,
  ...aria
}: RichTextEditorProps) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);

  const replaceSelection = (
    transform: (selected: string) => { text: string; selectionStart: number; selectionEnd: number },
  ): void => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end);
    const result = transform(selected);

    onChange(`${value.slice(0, start)}${result.text}${value.slice(end)}`);
    // Restore the caret after React has written the new value.
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + result.selectionStart, start + result.selectionEnd);
    });
  };

  const apply = (action: Action): void => {
    replaceSelection((selected) => {
      if (action.kind === 'wrap') {
        const text = `${action.before}${selected}${action.after}`;
        return {
          text,
          selectionStart: action.before.length,
          selectionEnd: action.before.length + selected.length,
        };
      }
      if (action.kind === 'prefix') {
        const lines = (selected.length > 0 ? selected : '').split('\n');
        const text = lines.map((line) => `${action.prefix}${line}`).join('\n');
        return { text, selectionStart: action.prefix.length, selectionEnd: text.length };
      }
      const text = `${action.fence}\n${selected}\n${action.fence}`;
      return {
        text,
        selectionStart: action.fence.length + 1,
        selectionEnd: action.fence.length + 1 + selected.length,
      };
    });
  };

  const insertImage = (markdown: string): void => {
    replaceSelection(() => ({
      text: markdown,
      selectionStart: markdown.length,
      selectionEnd: markdown.length,
    }));
  };

  const upload = async (files: File[]): Promise<void> => {
    const accepted = files.filter(isSupportedImage);
    if (accepted.length === 0) return;

    const tooBig = accepted.find((file) => !isWithinSizeLimit(file));
    if (tooBig) {
      toast.error(t('richText.imageTooLarge'));
      return;
    }

    setUploading(true);
    try {
      for (const file of accepted) {
        const attachment = await uploadAttachment(projectCode, file);
        insertImage(`\n![${attachment.fileName}](${attachment.url})\n`);
      }
    } catch (error) {
      toast.error(errorText(error, 'richText.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
    const files = imageFilesFrom(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    void upload(files);
  };

  const onDrop = (event: DragEvent<HTMLTextAreaElement>): void => {
    const files = imageFilesFrom(event.dataTransfer.files);
    if (files.length === 0) return;
    event.preventDefault();
    void upload(files);
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border bg-surface',
        invalid ? 'border-fail' : 'border-border',
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-canvas px-1 py-1">
        <ToolbarButton
          label={t('richText.bold')}
          onClick={() => apply({ kind: 'wrap', before: '**', after: '**' })}
          disabled={preview}
        >
          <Bold className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label={t('richText.italic')}
          onClick={() => apply({ kind: 'wrap', before: '*', after: '*' })}
          disabled={preview}
        >
          <Italic className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label={t('richText.heading')}
          onClick={() => apply({ kind: 'prefix', prefix: '## ' })}
          disabled={preview}
        >
          <Heading className="size-3.5" />
        </ToolbarButton>

        <span className="mx-1 h-5 w-px bg-border" />

        <ToolbarButton
          label={t('richText.bulletList')}
          onClick={() => apply({ kind: 'prefix', prefix: '- ' })}
          disabled={preview}
        >
          <List className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label={t('richText.numberedList')}
          onClick={() => apply({ kind: 'prefix', prefix: '1. ' })}
          disabled={preview}
        >
          <ListOrdered className="size-3.5" />
        </ToolbarButton>

        <span className="mx-1 h-5 w-px bg-border" />

        <ToolbarButton
          label={t('richText.inlineCode')}
          onClick={() => apply({ kind: 'wrap', before: '`', after: '`' })}
          disabled={preview}
        >
          <Code className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label={t('richText.codeBlock')}
          onClick={() => apply({ kind: 'block', fence: '```' })}
          disabled={preview}
        >
          <SquareCode className="size-3.5" />
        </ToolbarButton>

        <span className="mx-1 h-5 w-px bg-border" />

        <ToolbarButton
          label={t('richText.insertImage')}
          onClick={() => fileInputRef.current?.click()}
          disabled={preview || uploading}
        >
          {uploading ? <Spinner className="size-3.5" /> : <ImageIcon className="size-3.5" />}
        </ToolbarButton>

        <div className="ml-auto">
          <ToolbarButton
            label={preview ? t('richText.edit') : t('richText.preview')}
            onClick={() => setPreview((current) => !current)}
            active={preview}
          >
            {preview ? <Pencil className="size-3.5" /> : <Eye className="size-3.5" />}
          </ToolbarButton>
        </div>
      </div>

      {/* Zero-sized and in flow on purpose: `sr-only` is absolutely
          positioned, and inside the scrollable work area that stretches the
          document by the height of everything above the input. */}
      <input
        ref={fileInputRef}
        id={fileInputId}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        multiple
        tabIndex={-1}
        aria-hidden="true"
        className="size-0 overflow-hidden border-0 p-0"
        onChange={(event) => {
          void upload([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />

      {preview ? (
        <div className="min-h-24 px-2.5 py-2">
          {isBlank(value) ? (
            <p className="text-[13px] text-ink-subtle">{t('richText.nothingToPreview')}</p>
          ) : (
            <RichText value={value} />
          )}
        </div>
      ) : (
        <Textarea
          ref={textareaRef}
          id={id}
          rows={rows}
          value={value}
          aria-describedby={aria['aria-describedby']}
          aria-label={aria['aria-label']}
          placeholder={placeholder ?? t('richText.placeholder')}
          onChange={(event) => onChange(event.target.value)}
          onPaste={onPaste}
          onDrop={onDrop}
          className="rounded-none border-0 focus:outline-none"
        />
      )}
    </div>
  );
};
